import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

// ─── ICONS ──────────────────────────────────────────────────────────────────
const ReqIcon   = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 14h7v7"/></svg>;
const FlaskIcon = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M9 3h6M9 3v8l-4 9h14l-4-9V3"/></svg>;
const PillIcon  = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M10.5 20H4a2 2 0 01-2-2v-3.5a2 2 0 012-2h6.5"/><path d="M13.5 4H20a2 2 0 012 2v3.5a2 2 0 01-2 2h-6.5"/><path d="M10.5 20l9-16"/></svg>;
const DocIcon   = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8"/></svg>;
const ChatIcon  = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
const CardIcon  = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/></svg>;
const UserIcon  = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>;
const GridIcon  = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>;
const BellIcon  = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0"/></svg>;
const MenuIcon  = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 6h16M4 12h16M4 18h16"/></svg>;
const OutIcon   = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9"/></svg>;
const ShieldIcon= () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/></svg>;

const NAV = [
  { id: "dashboard",     label: "Dashboard",       Icon: GridIcon  },
  { id: "requisitions",  label: "E-Requisitions",  Icon: ReqIcon   },
  { id: "results",       label: "Lab Results",     Icon: FlaskIcon },
  { id: "prescriptions", label: "Prescriptions",   Icon: PillIcon  },
  { id: "records",       label: "Health Records",  Icon: DocIcon   },
  { id: "messages",      label: "Messages",        Icon: ChatIcon  },
  { id: "billing",       label: "Billing",         Icon: CardIcon  },
  { id: "profile",       label: "My Profile",      Icon: UserIcon  },
];

const fmtShort   = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";
const mkInitials = (name = "") => name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "??";

export default function Dashboard() {
  const navigate = useNavigate();
  const [active,        setActive]        = useState("dashboard");
  const [sideOpen,      setSideOpen]      = useState(true);
  const [now,           setNow]           = useState(new Date());
  const [user,          setUser]          = useState(null);
  const [appointments,  setAppointments]  = useState([]);
  const [results,       setResults]       = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [alerts,        setAlerts]        = useState([]);
  const [activity,      setActivity]      = useState([]);
  const [health,        setHealth]        = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("access");
    if (!token) { navigate("/login"); return; }
    api.get("dashboard/")
      .then(({ data: d }) => {
        setUser(d.user ?? null);
        setAppointments(d.appointments ?? []);
        setResults(d.results ?? []);
        setPrescriptions(d.prescriptions ?? []);
        setAlerts(d.alerts ?? []);
        setActivity(d.activity ?? []);
        setHealth(d.health ?? null);
      })
      .catch(err => {
        const s = err.response?.status;
        if (s === 401 || s === 403) {
          localStorage.removeItem("access");
          localStorage.removeItem("refresh");
          navigate("/login");
        } else {
          setError(err.response?.data?.detail || "Failed to load dashboard.");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const greet = () => {
    const h = now.getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const handleLogout = () => {
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    navigate("/login");
  };

  const upcomingApts   = appointments.filter(a => new Date(a.date) >= new Date());
  const flaggedResults = results.filter(r => r.flagged);
  const refillsDue     = prescriptions.filter(p => p.refills === 0);
  const displayName    = user?.full_name || user?.email || "User";
  const firstName      = displayName.split(" ")[0];
  const patientId      = user?.patient_id || "—";
  const userInitials   = mkInitials(displayName);
  const userRole       = user?.role || "patient";

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", fontFamily:"Inter,sans-serif", color:"#6b7280", fontSize:13 }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:32, height:32, border:"2px solid #e5e7eb", borderTopColor:"#00897B", borderRadius:"50%", animation:"spin .7s linear infinite", margin:"0 auto 12px" }}></div>
        Loading your dashboard…
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (error) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100vh", fontFamily:"Inter,sans-serif" }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:14, color:"#dc2626", marginBottom:12 }}>{error}</div>
        <button onClick={() => window.location.reload()} style={{ padding:"8px 16px", background:"#00897B", color:"#fff", border:"none", borderRadius:6, cursor:"pointer", fontSize:13 }}>Retry</button>
      </div>
    </div>
  );

  const W = sideOpen ? "210px" : "50px";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter', sans-serif; background: #f1f5f9; color: #111; font-size: 13px; -webkit-font-smoothing: antialiased; }
        button { font-family: inherit; cursor: pointer; }
        .app { display: flex; min-height: 100vh; }

        /* SIDEBAR */
        .sb { width: ${W}; background: #00897B; display: flex; flex-direction: column; position: fixed; top: 0; left: 0; bottom: 0; z-index: 200; transition: width .2s; overflow: hidden; }
        .sb-logo { height: 56px; display: flex; align-items: center; padding: 0 13px; gap: 9px; border-bottom: 1px solid rgba(255,255,255,0.1); flex-shrink: 0; }
        .sb-logo-box { width: 28px; height: 28px; border-radius: 7px; background: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .sb-logo-text { overflow: hidden; }
        .sb-logo-name { font-size: 13px; font-weight: 700; color: #fff; white-space: nowrap; line-height: 1.1; opacity: ${sideOpen ? 1 : 0}; transition: opacity .15s; }
        .sb-logo-sub { font-size: 9.5px; color: rgba(255,255,255,0.45); white-space: nowrap; opacity: ${sideOpen ? 1 : 0}; transition: opacity .15s; }
        .sb-nav { flex: 1; padding: 10px 7px; overflow-y: auto; overflow-x: hidden; scrollbar-width: none; }
        .sb-grp { font-size: 9.5px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: rgba(255,255,255,0.3); padding: 8px 8px 3px; white-space: nowrap; opacity: ${sideOpen ? 1 : 0}; transition: opacity .15s; }
        .nb { width: 100%; display: flex; align-items: center; gap: 8px; padding: 8px 9px; border-radius: 7px; border: none; background: none; color: rgba(255,255,255,0.5); font-size: 12.5px; font-weight: 500; text-align: left; white-space: nowrap; margin-bottom: 1px; transition: background .1s, color .1s; }
        .nb:hover { background: rgba(255,255,255,0.1); color: rgba(255,255,255,0.9); }
        .nb.on { background: rgba(255,255,255,0.15); color: #fff; font-weight: 600; }
        .nav-ic { flex-shrink: 0; width: 15px; height: 15px; display: flex; align-items: center; justify-content: center; }
        .nav-lbl { flex: 1; opacity: ${sideOpen ? 1 : 0}; transition: opacity .15s; white-space: nowrap; }
        .sb-foot { border-top: 1px solid rgba(255,255,255,0.1); padding: 8px 7px; flex-shrink: 0; }
        .sb-user { display: flex; align-items: center; gap: 8px; padding: 7px 9px; border-radius: 7px; }
        .sb-av { width: 28px; height: 28px; border-radius: 50%; background: rgba(255,255,255,0.18); display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; color: #fff; flex-shrink: 0; }
        .sb-uinfo { opacity: ${sideOpen ? 1 : 0}; transition: opacity .15s; overflow: hidden; }
        .sb-uname { font-size: 12px; font-weight: 600; color: #fff; white-space: nowrap; }
        .sb-urole { font-size: 10px; color: rgba(255,255,255,0.35); white-space: nowrap; text-transform: capitalize; }
        .sb-out { width: 100%; display: flex; align-items: center; gap: 8px; padding: 7px 9px; border-radius: 7px; border: none; background: none; color: rgba(255,255,255,0.3); font-size: 12px; margin-top: 2px; transition: background .1s, color .1s; }
        .sb-out:hover { background: rgba(239,68,68,0.15); color: #fca5a5; }
        .sb-out-lbl { opacity: ${sideOpen ? 1 : 0}; transition: opacity .15s; white-space: nowrap; }

        /* MAIN */
        .main { margin-left: ${W}; flex: 1; display: flex; flex-direction: column; transition: margin-left .2s; min-width: 0; }

        /* TOPBAR */
        .topbar { height: 56px; background: #fff; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: space-between; padding: 0 18px; position: sticky; top: 0; z-index: 100; flex-shrink: 0; }
        .tb-l { display: flex; align-items: center; gap: 10px; }
        .tb-toggle { width: 30px; height: 30px; border-radius: 6px; border: 1px solid #e5e7eb; background: none; display: flex; align-items: center; justify-content: center; color: #6b7280; transition: background .1s; }
        .tb-toggle:hover { background: #f3f4f6; }
        .tb-title { font-size: 13.5px; font-weight: 700; color: #0f172a; }
        .tb-sub { font-size: 11px; color: #9ca3af; }
        .tb-r { display: flex; align-items: center; gap: 7px; }
        .tb-badge { display: flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 20px; background: #E0F2F1; font-size: 10.5px; font-weight: 600; color: #00897B; }
        .tb-badge svg { width: 11px; height: 11px; stroke: #00897B; fill: none; stroke-width: 2; }
        .tb-ic { width: 30px; height: 30px; border-radius: 6px; border: 1px solid #e5e7eb; background: none; display: flex; align-items: center; justify-content: center; color: #6b7280; position: relative; transition: background .1s; }
        .tb-ic:hover { background: #f3f4f6; }
        .pip { position: absolute; top: 5px; right: 5px; width: 5px; height: 5px; border-radius: 50%; background: #dc2626; border: 1.5px solid #fff; }
        .tb-av { width: 30px; height: 30px; border-radius: 50%; background: #00897B; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; color: #fff; cursor: pointer; }

        /* CONTENT */
        .content { padding: 16px; flex: 1; }

        /* WELCOME */
        .welcome { background: #00897B; border-radius: 12px; padding: 16px 20px; display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
        .wn { font-size: 16px; font-weight: 700; margin-bottom: 3px; color: #fff; }
        .wm { font-size: 11.5px; color: rgba(255,255,255,0.55); }
        .wb { display: flex; gap: 7px; flex-shrink: 0; }
        .btn-white { background: #fff; color: #00897B; border: none; padding: 7px 14px; border-radius: 7px; font-size: 12px; font-weight: 600; transition: opacity .12s; }
        .btn-white:hover { opacity: .9; }
        .btn-white-outline { background: rgba(255,255,255,0.1); color: #fff; border: 1px solid rgba(255,255,255,0.25); padding: 7px 14px; border-radius: 7px; font-size: 12px; font-weight: 500; transition: background .12s; }
        .btn-white-outline:hover { background: rgba(255,255,255,0.18); }

        /* STATS */
        .stats { display: grid; grid-template-columns: repeat(4,1fr); gap: 10px; margin-bottom: 14px; }
        .stat { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 13px 15px; cursor: pointer; transition: border-color .15s, transform .1s; }
        .stat:hover { border-color: #00897B; transform: translateY(-1px); }
        .stat-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
        .stat-lbl { font-size: 11px; color: #6b7280; font-weight: 500; }
        .badge { font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 20px; }
        .bg { background: #f0fdf4; color: #16a34a; }
        .by { background: #fefce8; color: #ca8a04; }
        .br { background: #fef2f2; color: #dc2626; }
        .stat-n { font-size: 28px; font-weight: 700; color: #0f172a; line-height: 1; }

        /* GRID */
        .grid2 { display: grid; grid-template-columns: 1fr 265px; gap: 12px; }
        .col { display: flex; flex-direction: column; gap: 10px; }

        /* CARD */
        .card { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; }
        .ch { display: flex; align-items: center; justify-content: space-between; padding: 13px 15px 0; }
        .ct { font-size: 13px; font-weight: 700; color: #0f172a; }
        .cb { padding: 8px 15px 13px; }
        .btn-ghost { font-size: 11px; font-weight: 500; padding: 4px 9px; border-radius: 6px; border: 1px solid #e5e7eb; background: none; color: #6b7280; transition: background .1s; }
        .btn-ghost:hover { background: #f3f4f6; color: #111; }
        .btn-fill { font-size: 11px; font-weight: 600; padding: 4px 9px; border-radius: 6px; border: none; background: #00897B; color: #fff; transition: background .1s; }
        .btn-fill:hover { background: #00695C; }
        .empty { padding: 14px 0; text-align: center; font-size: 12px; color: #d1d5db; }

        /* APPOINTMENTS */
        .apt { display: flex; gap: 10px; padding: 9px 0; border-bottom: 1px solid #f3f4f6; }
        .apt:last-child { border-bottom: none; padding-bottom: 0; }
        .apt-ic { width: 34px; height: 34px; border-radius: 8px; flex-shrink: 0; background: #E0F2F1; display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 700; color: #00897B; }
        .apt-name { font-size: 12.5px; font-weight: 600; color: #0f172a; margin-bottom: 1px; }
        .apt-spec { font-size: 11px; color: #9ca3af; margin-bottom: 3px; }
        .apt-type { font-size: 10px; font-weight: 500; padding: 2px 7px; border-radius: 20px; background: #f1f5f9; color: #64748b; display: inline-block; }
        .apt-when { text-align: right; flex-shrink: 0; }
        .apt-date { font-size: 12px; font-weight: 600; color: #0f172a; }
        .apt-time { font-size: 10.5px; color: #9ca3af; margin-top: 1px; }

        /* LAB RESULTS */
        .res { display: flex; align-items: center; gap: 9px; padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
        .res:last-child { border-bottom: none; padding-bottom: 0; }
        .rdot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
        .dg { background: #16a34a; } .dy { background: #ca8a04; }
        .res-name { font-size: 12.5px; font-weight: 600; color: #0f172a; margin-bottom: 1px; }
        .res-meta { font-size: 11px; color: #9ca3af; }
        .flag { font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 20px; background: #fefce8; color: #ca8a04; margin-left: auto; flex-shrink: 0; }
        .vbtn { font-size: 11px; font-weight: 500; padding: 3px 8px; border-radius: 5px; border: 1px solid #e5e7eb; background: none; color: #6b7280; transition: background .1s; }
        .vbtn:hover { background: #f3f4f6; }

        /* ACTIVITY */
        .act { display: flex; gap: 9px; padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
        .act:last-child { border-bottom: none; padding-bottom: 0; }
        .act-pip { width: 6px; height: 6px; border-radius: 50%; background: #cbd5e1; flex-shrink: 0; margin-top: 4px; }
        .act-text { font-size: 12.5px; font-weight: 600; color: #0f172a; margin-bottom: 1px; }
        .act-sub { font-size: 11px; color: #6b7280; }
        .act-date { font-size: 10.5px; color: #9ca3af; margin-top: 1px; }

        /* ALERTS */
        .al { display: flex; gap: 8px; padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
        .al:last-child { border-bottom: none; padding-bottom: 0; }
        .al-bar { width: 3px; border-radius: 2px; flex-shrink: 0; align-self: stretch; min-height: 18px; }
        .bw { background: #ca8a04; } .bi { background: #2563eb; }
        .al-text { font-size: 12px; font-weight: 500; color: #0f172a; margin-bottom: 1px; }
        .al-date { font-size: 10.5px; color: #9ca3af; }

        /* PRESCRIPTIONS */
        .rx { display: flex; align-items: center; gap: 8px; padding: 8px 0; border-bottom: 1px solid #f3f4f6; }
        .rx:last-child { border-bottom: none; padding-bottom: 0; }
        .rx-bar { width: 3px; border-radius: 2px; align-self: stretch; min-height: 26px; flex-shrink: 0; }
        .rx-name { font-size: 12.5px; font-weight: 600; color: #0f172a; margin-bottom: 1px; }
        .rx-dose { font-size: 10.5px; color: #6b7280; }
        .rx-tag { font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 20px; white-space: nowrap; }
        .tg { background: #f0fdf4; color: #16a34a; }
        .ty { background: #fefce8; color: #ca8a04; }
        .tr { background: #fef2f2; color: #dc2626; }

        /* HEALTH SUMMARY */
        .hgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; margin-bottom: 12px; }
        .hs { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 7px; padding: 9px 11px; }
        .hs-l { font-size: 9.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: #9ca3af; margin-bottom: 2px; }
        .hs-v { font-size: 16px; font-weight: 700; color: #0f172a; }
        .sec-lbl { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: #9ca3af; margin: 8px 0 5px; }
        .tags { display: flex; flex-wrap: wrap; gap: 4px; }
        .tag { font-size: 10.5px; font-weight: 500; padding: 2px 8px; border-radius: 20px; }
        .tag-r { background: #fef2f2; color: #dc2626; }
        .tag-p { background: #f5f3ff; color: #7c3aed; }

        /* HIPAA NOTICE */
        .hipaa-bar { display: flex; align-items: center; gap: 7px; background: #E0F2F1; border: 1px solid #80CBC4; border-radius: 8px; padding: 9px 13px; margin-bottom: 14px; font-size: 11.5px; color: #00897B; font-weight: 500; }
        .hipaa-bar svg { width: 13px; height: 13px; stroke: #00897B; fill: none; stroke-width: 2; flex-shrink: 0; }

        /* TIP */
        .tip { background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px 14px; }
        .tip-l { font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #9ca3af; margin-bottom: 5px; }
        .tip-t { font-size: 12px; color: #6b7280; line-height: 1.65; }

        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 1024px) { .stats { grid-template-columns: repeat(2,1fr); } .grid2 { grid-template-columns: 1fr; } }
        @media (max-width: 600px) { .content { padding: 10px; } .wb { display: none; } .stats { grid-template-columns: repeat(2,1fr); } .hipaa-bar { display: none; } }
      `}</style>

      <div className="app">

        {/* SIDEBAR */}
        <aside className="sb">
          <div className="sb-logo">
            <div className="sb-logo-box">
              <img src="/micronlabs.png" alt="MicronLabs" style={{ width:"18px", height:"18px", objectFit:"contain" }}/>
            </div>
            <div className="sb-logo-text">
              <div className="sb-logo-name">MicronLabs</div>
              <div className="sb-logo-sub">Laboratory Information System</div>
            </div>
          </div>

          <nav className="sb-nav">
            <div className="sb-grp">Main</div>
            {NAV.slice(0, 5).map(({ id, label, Icon }) => (
              <button key={id} className={`nb ${active === id ? "on" : ""}`}
                onClick={() => { setActive(id); navigate(`/${id}`); }}>
                <span className="nav-ic"><Icon /></span>
                <span className="nav-lbl">{label}</span>
              </button>
            ))}
            <div className="sb-grp" style={{ marginTop: 8 }}>Account</div>
            {NAV.slice(5).map(({ id, label, Icon }) => (
              <button key={id} className={`nb ${active === id ? "on" : ""}`}
                onClick={() => { setActive(id); navigate(`/${id}`); }}>
                <span className="nav-ic"><Icon /></span>
                <span className="nav-lbl">{label}</span>
              </button>
            ))}
          </nav>

          <div className="sb-foot">
            <div className="sb-user">
              <div className="sb-av">{userInitials}</div>
              <div className="sb-uinfo">
                <div className="sb-uname">{displayName}</div>
                <div className="sb-urole">{userRole} · #{patientId}</div>
              </div>
            </div>
            <button className="sb-out" onClick={handleLogout}>
              <OutIcon /><span className="sb-out-lbl">Sign out</span>
            </button>
          </div>
        </aside>

        {/* MAIN */}
        <div className="main">
          <header className="topbar">
            <div className="tb-l">
              <button className="tb-toggle" onClick={() => setSideOpen(o => !o)}><MenuIcon /></button>
              <div>
                <div className="tb-title">{NAV.find(n => n.id === active)?.label || "Dashboard"}</div>
                <div className="tb-sub">{now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div>
              </div>
            </div>
            <div className="tb-r">
              <div className="tb-badge">
                <ShieldIcon/>
                HIPAA secured
              </div>
              <button className="tb-ic">
                <BellIcon />
                {alerts.length > 0 && <span className="pip" />}
              </button>
              <div className="tb-av">{userInitials}</div>
            </div>
          </header>

          <div className="content">

            {/* HIPAA NOTICE BAR */}
            <div className="hipaa-bar">
              <svg viewBox="0 0 24 24"><path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/><path d="M9 12l2 2 4-4"/></svg>
              Your health information is protected under HIPAA. MicronLabs uses 256-bit encryption and role-based access controls.
            </div>

            {/* WELCOME */}
            <div className="welcome">
              <div>
                <div className="wn">{greet()}, {firstName}</div>
                <div className="wm">
                  Patient ID #{patientId} &nbsp;·&nbsp;
                  {now.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                </div>
              </div>
              <div className="wb">
                <button className="btn-white" onClick={() => navigate("/requisitions")}>+ New test order</button>
                <button className="btn-white-outline" onClick={() => navigate("/records")}>View records</button>
              </div>
            </div>

            {/* STATS */}
            <div className="stats">
              {[
                { label:"Test orders",    n:appointments.length,  badge:`${upcomingApts.length} pending`,                                              cls:"bg", onClick:() => setActive("requisitions") },
                { label:"Lab results",    n:results.length,       badge:flaggedResults.length > 0 ? `${flaggedResults.length} flagged` : "All clear",  cls:flaggedResults.length > 0 ? "by" : "bg", onClick:() => setActive("results") },
                { label:"Prescriptions",  n:prescriptions.length, badge:refillsDue.length > 0 ? `${refillsDue.length} refill due` : "All stocked",     cls:refillsDue.length > 0 ? "by" : "bg", onClick:() => setActive("prescriptions") },
                { label:"Alerts",         n:alerts.length,        badge:alerts.length > 0 ? `${alerts.length} active` : "None",                        cls:alerts.length > 0 ? "br" : "bg" },
              ].map(({ label, n, badge, cls, onClick }) => (
                <div key={label} className="stat" onClick={onClick}>
                  <div className="stat-row">
                    <span className="stat-lbl">{label}</span>
                    <span className={`badge ${cls}`}>{badge}</span>
                  </div>
                  <div className="stat-n">{n}</div>
                </div>
              ))}
            </div>

            {/* MAIN GRID */}
            <div className="grid2">
              <div className="col">

                {/* TEST ORDERS */}
                <div className="card">
                  <div className="ch">
                    <span className="ct">Pending test orders</span>
                    <button className="btn-fill" onClick={() => navigate("/requisitions")}>+ New order</button>
                  </div>
                  <div className="cb">
                    {upcomingApts.length === 0
                      ? <div className="empty">No pending test orders</div>
                      : upcomingApts.slice(0, 3).map(a => (
                        <div key={a.id} className="apt">
                          <div className="apt-ic">{mkInitials(a.doctor)}</div>
                          <div style={{ flex:1 }}>
                            <div className="apt-name">{a.doctor}</div>
                            <div className="apt-spec">{a.specialty}{a.location ? ` · ${a.location}` : ""}</div>
                            <span className="apt-type">{a.type}</span>
                          </div>
                          <div className="apt-when">
                            <div className="apt-date">{fmtShort(a.date)}</div>
                            <div className="apt-time">{a.time}</div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>

                {/* LAB RESULTS */}
                <div className="card">
                  <div className="ch">
                    <span className="ct">Lab results</span>
                    <button className="btn-ghost" onClick={() => navigate("/results")}>View all</button>
                  </div>
                  <div className="cb">
                    {results.length === 0
                      ? <div className="empty">No lab results on file</div>
                      : results.slice(0, 4).map(r => (
                        <div key={r.id} className="res">
                          <div className={`rdot ${r.flagged ? "dy" : "dg"}`} />
                          <div style={{ flex:1 }}>
                            <div className="res-name">{r.name}</div>
                            <div className="res-meta">{fmtShort(r.date)} · {r.status}</div>
                          </div>
                          {r.flagged && <span className="flag">Review</span>}
                          <button className="vbtn">View PDF</button>
                        </div>
                      ))
                    }
                  </div>
                </div>

                {/* ACTIVITY */}
                <div className="card">
                  <div className="ch">
                    <span className="ct">Recent activity</span>
                    <button className="btn-ghost">See all</button>
                  </div>
                  <div className="cb">
                    {activity.length === 0
                      ? <div className="empty">No recent activity</div>
                      : activity.slice(0, 5).map(a => (
                        <div key={a.id} className="act">
                          <div className="act-pip" />
                          <div>
                            <div className="act-text">{a.text}</div>
                            <div className="act-sub">{a.sub}</div>
                            <div className="act-date">{a.date}</div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>

              </div>

              {/* RIGHT COLUMN */}
              <div className="col">

                {/* ALERTS */}
                <div className="card">
                  <div className="ch">
                    <span className="ct">Alerts</span>
                    {alerts.length > 0 && (
                      <span style={{ fontSize:10, fontWeight:700, padding:"2px 7px", borderRadius:20, background:"#fef2f2", color:"#dc2626" }}>
                        {alerts.length}
                      </span>
                    )}
                  </div>
                  <div className="cb">
                    {alerts.length === 0
                      ? <div className="empty">No active alerts</div>
                      : alerts.map(a => (
                        <div key={a.id} className="al">
                          <div className={`al-bar ${a.level === "warn" ? "bw" : "bi"}`} />
                          <div>
                            <div className="al-text">{a.text}</div>
                            <div className="al-date">{a.date}</div>
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>

                {/* PRESCRIPTIONS */}
                <div className="card">
                  <div className="ch">
                    <span className="ct">Prescriptions</span>
                    <button className="btn-ghost" onClick={() => navigate("/prescriptions")}>All</button>
                  </div>
                  <div className="cb">
                    {prescriptions.length === 0
                      ? <div className="empty">No active prescriptions</div>
                      : prescriptions.slice(0, 4).map(p => (
                        <div key={p.id} className="rx">
                          <div className="rx-bar" style={{ background: p.refills === 0 ? "#dc2626" : p.refills <= 1 ? "#ca8a04" : "#16a34a" }} />
                          <div style={{ flex:1 }}>
                            <div className="rx-name">{p.name}</div>
                            <div className="rx-dose">{p.dose}</div>
                          </div>
                          <span className={`rx-tag ${p.refills === 0 ? "tr" : p.refills <= 1 ? "ty" : "tg"}`}>
                            {p.refills === 0 ? "Refill now" : `${p.refills} refills`}
                          </span>
                        </div>
                      ))
                    }
                  </div>
                </div>

                {/* HEALTH SUMMARY */}
                <div className="card">
                  <div className="ch">
                    <span className="ct">Health summary</span>
                    <button className="btn-ghost">Edit</button>
                  </div>
                  <div className="cb">
                    {!health
                      ? <div className="empty">No health data on file</div>
                      : (
                        <>
                          <div className="hgrid">
                            {[
                              ["Blood type", health.bloodType],
                              ["BMI",        health.bmi],
                              ["Weight",     health.weight],
                              ["Height",     health.height],
                            ].map(([l, v]) => (
                              <div key={l} className="hs">
                                <div className="hs-l">{l}</div>
                                <div className="hs-v">{v || "—"}</div>
                              </div>
                            ))}
                          </div>
                          {health.allergies?.length > 0 && (
                            <>
                              <div className="sec-lbl">Allergies</div>
                              <div className="tags">{health.allergies.map((a, i) => <span key={i} className="tag tag-r">{a}</span>)}</div>
                            </>
                          )}
                          {health.conditions?.length > 0 && (
                            <>
                              <div className="sec-lbl">Diagnoses</div>
                              <div className="tags">{health.conditions.map((c, i) => <span key={i} className="tag tag-p">{c}</span>)}</div>
                            </>
                          )}
                          {health.emergency && (
                            <>
                              <div className="sec-lbl">Emergency contact</div>
                              <div style={{ fontSize:12, fontWeight:600, color:"#0f172a" }}>{health.emergency}</div>
                            </>
                          )}
                        </>
                      )
                    }
                  </div>
                </div>

                {/* HEALTH TIP */}
                <div className="tip">
                  <div className="tip-l">Health tip</div>
                  <div className="tip-t">Stay current with your preventive screenings. The CDC recommends annual wellness visits to catch conditions early and keep your health records up to date.</div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}